import { useMemo } from "react";

import TitleAnnulmentsPanel from "../../components/titleAnnulments/TitleAnnulmentsPanel";

import MetricCard from "../../components/director/module1/MetricCard";
import ModuleShell from "../../components/director/module1/ModuleShell";
import ModuleState from "../../components/director/module1/ModuleState";
import useDirectorSurface from "../../components/director/module1/useDirectorSurface";
import { useDirectorContext } from "@contexts/director";
import {
  previewDirectorTitle, reincludeDirectorTitle, reprocessDirectorTitles, withdrawDirectorTitle,
} from "@contexts/director/api";
import { formatBps, formatCostCenterRole } from "../../utils/directorFormat";
import { currentCompetence, moduleFilters } from "./module1Utils";

const DIRECTOR_ANNULMENT_API = {
  preview: previewDirectorTitle,
  annul: withdrawDirectorTitle,
  revoke: reincludeDirectorTitle,
  reprocess: reprocessDirectorTitles,
};

const DataGovernance = ({ portfolioOnly = false }) => {
  const { filters, selectedCompanyId } = useDirectorContext();
  const query = useMemo(() => moduleFilters({ from: currentCompetence(), months: 24, filters }), [filters]);
  const coverage = useDirectorSurface("governance/coverage", query);
  const portfolio = useDirectorSurface("governance/portfolio", query);
  const exclusionsQuery = useMemo(() => ({}), []);
  const exclusions = useDirectorSurface("title-exclusions", exclusionsQuery);
  const data = coverage.data;
  const mapping = portfolio.data;
  return <ModuleShell
    title={portfolioOnly ? "Configurações de portfólio e caixas" : "Governança e cobertura dos dados"}
    description="Fonte, competência, frescor e classificação acompanham cada leitura. Ausência nunca vira zero."
    badge={data?.coverage?.valueBps ? `Cobertura ${formatBps(data.coverage.valueBps)}` : "Cobertura em atualização"}
    qualitySources={[coverage, portfolio]}
  >
    <ModuleState loading={coverage.loading || portfolio.loading} error={coverage.error || portfolio.error} availability={mapping ? "available" : "empty"} onRetry={() => { coverage.reload(); portfolio.reload(); }} />
    {data && mapping && <>
      <section className="director-module1__metrics">
        <MetricCard label="Cobertura por valor" value={formatBps(data.coverage?.valueBps)} note="Escopo do portfólio" tone="green" />
        <MetricCard label="Centros inferidos" value={data.inferred?.costCenterCount ?? 0} note="Aguardam revisão humana" tone="orange" />
        <MetricCard label="Regime de saldo" value={data.balanceRegime === "declared" ? "Declarado" : "Sem saldo"} note="Saldo bancário real não disponível" tone="blue" />
      </section>
      <section className="director-module1__split">
        <article className="director-module1__panel"><header><div><span className="director-eyebrow">{portfolioOnly ? "Mapeamento financeiro" : "Saúde das fontes"}</span><h2>{mapping.name}</h2></div></header>{portfolioOnly ? <>
          <div className="director-table-scroll"><table className="director-table"><thead><tr><th>Centro</th><th>Papel</th><th>Entidade</th><th>Classificação</th></tr></thead><tbody>{(mapping.costCenters || []).map((item) => <tr key={`${item.legalEntityId}-${item.id}`}><td>{item.name || `Centro ${item.id}`}</td><td>{formatCostCenterRole(item.role)}</td><td>{item.legalEntityId}</td><td>{item.classificationState || "unknown"}</td></tr>)}</tbody></table></div>
          <div className="director-module1__scenario-grid">{Object.entries(mapping.cashflowScenarios || {}).map(([name, values]) => <article key={name}><strong>{name}</strong><span>Atraso: {values.inflowDelayDays} dias</span><span>Corte: {(values.futureInflowHaircutBps / 100).toFixed(1)}%</span></article>)}</div>
        </> : <div className="director-module1__source-list">
          <div><span>Contas a receber</span><strong>{formatBps(data.coverage?.valueBps)}</strong></div>
          <div><span>Contas a pagar</span><strong>{formatBps(data.coverage?.valueBps)}</strong></div>
          <div><span>Movimentos bancários</span><strong>{data.limitations?.includes("bank_movements_missing") ? "Indisponível" : "Evidência separada"}</strong></div>
          <div data-restricted><span>Saldo bancário real</span><strong>Indisponível · Módulo 2</strong></div>
          <div data-restricted><span>DRE contábil</span><strong>Indisponível · Módulo 4</strong></div>
        </div>}</article>
        <aside className="director-module1__insight"><span className="director-eyebrow">Qualidade é parte do produto</span><h2>{data.limitations?.length ? `${data.limitations.length} limitação(ões) explícita(s)` : "Fontes completas para o recorte"}</h2><p>Cada número carrega revisão e cobertura. Classificações inferidas continuam visíveis até aprovação.</p><ul className="director-module1__limitations">{(data.limitations || []).map((item) => <li key={item}>{item.replaceAll("_", " ")}</li>)}</ul></aside>
      </section>
    </>}
    {!portfolioOnly && <TitleAnnulmentsPanel variant="director" data={exclusions.data} reload={exclusions.reload} api={DIRECTOR_ANNULMENT_API} companyId={selectedCompanyId} />}
  </ModuleShell>;
};

export default DataGovernance;
